const CURRENCY = "USD";

// package imports
import Head from "next/head";

//local imports
import { constructLink, convertEntityKeyToName } from "@/utils/linkConstructor";
import { connectToCarDataDB } from "@/db/connectCarData";
import { connectToDatabase } from "@/db/connectDb";
import SearchBox from "@/components/common/search-box";
import BreadCrumbList from "@/components/airport/dynamic-bread-crumb";
import RentalPartners from "@/components/common/search-box/RentalPartners";
import OtherRentalCompanies from "@/components/inner/other-rental-companies";
import {
  returnElectricCarCount,
  generateFullClassLinksFromArray,
  generateInnerAgencyLinks,
  generateInnerBrandLinks,
  generateInnerClassLinks,
} from "@/utils/generateInnerLinks";
import AvailableClasses from "@/components/airport/available-classes";
import BestCarsList from "@/components/inner/car-list-best";
import PopularCarList from "@/components/inner/car-list-popular";
import CheapCarsList from "@/components/inner/car-list-cheapest";
import ClassMap from "@/components/inner/class-map";
import ClassCarFeatures from "@/components/inner/class-features";
import CustomFAQ from "@/components/inner/custom-faq";
import Footer from "@/components/layout/footer";
import { fetchNearestAirportData } from "@/utils/staticDataFetch";
import AirportsNearYou from "@/components/airport/near-you";
import AvailableBrands from "@/components/common/car-brand-list";
import { getUniqueAgencies } from "@/utils/uniqueParse";

type Props = {
  airportNameData: string;
  carArray: string;
  classCode: string;
  airport: string;
  locationName: string;
  url: string;
  class: string;
  locale: string;
  agencyList: string;
  classList: string[];
  brandList: string[];
  updatedAt: string;
  nearestAirports: string;
  carClassName: string;
  hasElectricCars: boolean;
  rating: {
    ratingValue: number;
    ratingCount: number;
    bestRating: number;
  };
};

const ClassPage = (props: Props) => {
  const airportNameData: AirportNameData = JSON.parse(props.airportNameData);
  const carArray: CarData[] = JSON.parse(props.carArray).map((car: CarData) => ({
    ...car, shareURL: car.shareURL ? Buffer.from(car.shareURL, "base64").toString("utf8") : car.shareURL,
  }));
  const airport = JSON.parse(props.airport);
  const carClassName = carArray[0].car.carclass;
  const agencyList: Agency[] = JSON.parse(props.agencyList);
  const nearestAirports = JSON.parse(props.nearestAirports);
  const featureData = [
    {
      title: `${carArray[0].agency.name}`,
      subtitle: "Most Popular Agency",
      desc: `Of all the cars ${carArray[0].agency.name} has at ${props.locationName}, our users have chosen ${carArray[0].agency.name} as their favorite choice.`,
    },
    {
      title: `${carArray[0].car.brand.split(" ")[0]}`,
      subtitle: "Most Popular Car Brand",
      desc: `Our data shows that ${carArray[0].car.brand} is the popular choice among our users. You can now book ${carArray[0].car.brand} or a similar one from ${carArray[0].agency.name} below.`,
    },
    {
      title: `${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].car.brand
        }`,
      subtitle: "Cheapest Car Model",
      desc: `If you are looking for a cheap car, try ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].car.brand
        }. This is the most affordable car available at ${props.locationName
        } from ${carArray[0].agency.name}.`,
    },
    {
      title: `${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
        .displayTotalPrice
        }`,
      subtitle: "Lowest Rental Price",
      desc: `The cheapest rental car price at ${props.locationName
        } for a rental car by ${carArray[0].agency.name} is ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
          .displayTotalPrice
        }/day. The average cost of a rental car at ${props.locationName} is ${carArray[0].displayTotalPrice[0]
        }${Math.ceil(
          carArray.reduce((a, { totalPrice }) => a + totalPrice, 0) /
          carArray.length
        )}/day.`,
    },
  ];

  const faqData = [
    {
      id: 1,
      question: `What is the most rented ${props.carClassName} car in ${props.locationName}?`,
      ans: `The most rented ${props.carClassName} car in ${props.locationName} is ${carArray[0].car.brand}.`,
    },
    {
      id: 2,
      question: `How much does it cost to rent a ${props.carClassName} car in ${props.locationName}?`,
      ans: `The average cost to rent a ${props.carClassName} car in ${props.locationName
        } is ${carArray[0].displayTotalPrice[0]}${Math.ceil(
          carArray.reduce((a, { totalPrice }) => a + totalPrice, 0) /
          carArray.length
        )}/day.`,
    },
    {
      id: 3,
      question: `Which company is the cheapest for ${props.carClassName} car rentals at ${props.locationName}?`,
      ans: `${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].agency.name
        } provides the cheapest rental deals for ${carArray[0].car.carclass
        } cars at ${props.locationName}.`,
    },
    {
      id: 4,
      question: `What is the most popular and reliable ${props.carClassName} car rental company in ${props.locationName}?`,
      ans: `${carArray.sort((a, b) => a.rankingScore - b.rankingScore)[0].agency.name
        } is the most popular and reliable ${props.carClassName
        } car rental company in ${props.locationName}.)}`,
    },
  ];

  const breadCrumbData = [
    {
      name: "Car Rentals",
      url: "/",
    },
    {
      name: "Airports in USA",
      url: "/airports-in-united-states",
    },
    {
      name: `Car Rentals at ${airportNameData.shortCode} Airport`,
      url: `/${props.url}`,
    },
    {
      name: `${carArray[0].car.carclass}`,
      url: `/${props.url}/class/${props.class}`,
    },
  ];

  const productSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${props.locationName.replace(/International/g, "Intl")} Car Rentals`,
    image: "https://www.rent80.com/og-image.png",
    description: `Car Rentals at ${props.locationName.replace(
      /International/g,
      "Intl"
    )}. Compare and Find Best Car rental deals in nearby Airports.`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: props.rating.ratingValue,
      ratingCount: props.rating.ratingCount,
      bestRating: props.rating.bestRating,
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: CURRENCY,
      lowPrice: Math.floor(
        carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].totalPrice
      ),
      highPrice: Math.ceil(
        carArray
          .sort((a: CarData, b: CarData) => a.totalPrice - b.totalPrice)
          .slice(-1)[0]?.totalPrice
      ),
      offerCount: carArray.length,
    },
  };

  const breadcrumbSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com",
          name: "Car Rentals",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com/airports-in-united-states",
          name: "Airports in USA",
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}`,
          name: `Car Rentals at ${airportNameData.shortCode} Airport`,
        },
      },
      {
        "@type": "ListItem",
        position: 4,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}/class/${props.class}`,
          name: `${carArray[0].car.carclass}`,
        },
      },
    ],
  };
  return (
    <>
      <Head>
        <title>{`${props.carClassName
          } Car Rentals in ${props.locationName.replace(
            /International/g,
            "Intl"
          )}`}</title>
        <meta
          name="description"
          content={`${props.carClassName
            } Car Rentals in ${props.locationName.replace(
              /International/g,
              "Intl"
            )}. Compare and Find Best Car rental deals in nearby Airports.`}
        />
        {typeof window !== "undefined" && (
          <>
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbSchemaJSON)}
            </script>
            <script type="application/ld+json">
              {JSON.stringify(productSchemaJSON)}
            </script>
          </>
        )}
        <link rel="image_src" href="/og-image.png" />
        <meta property="og:image" content="https://www.rent80.com/og-image.png" />
        <meta
          property="og:title"
          content={`${props.carClassName
            } Car Hire at ${props.locationName.replace(
              /International/g,
              "Intl"
            )}`}
        />
        <meta
          property="og:description"
          content={`${props.carClassName
            } Car Hire at ${props.locationName.replace(
              /International/g,
              "Intl"
            )}. Compare and Find Best Car rental deals in nearby Airports.`}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent80" />
        <meta
          property="og:url"
          content={`https://www.rent80.com/${props.url}/class/${props.class}`}
        />
        {/* {
          locales.map(locale => (
            <link rel="alternate" hrefLang={locale} href={`https://www.rent80.com/${locale}/${url}`} key={locale} />
          ))
        }
        <link rel="alternate" href="https://www.rent80.com/${url}" hrefLang="x-default" /> */}
        <link
          rel="canonical"
          href={`https://www.rent80.com/${props.url}/class/${props.class}`}
        />
      </Head>
      <main className="bg-[#F1F2F7] min-h-screen w-full overflow-x-clip">
        <SearchBox
          locale={props.locale}
          headText={`${carArray[0].car.carclass} Car Rentals in ${props.locationName}`}
          type={"airport"}
          locationName={props.locationName}
          ctid={airport.ctid}
          airport={`${airport.apicode}_class_${props.class}`}
          airportData={airportNameData}
        />
        <BreadCrumbList breadcrumbs={breadCrumbData} />
        <RentalPartners
          type="agency"
          agencyData={getUniqueAgencies(carArray)}
          locationName={props.locationName}
        />
        <BestCarsList
          title={`Best deals for ${carArray[0].car.carclass} Car retals in ${props.locationName}`}
          subtitle={`Find the best deals for ${carArray[0].car.carclass} Car rentals in ${props.locationName}.`}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={props.updatedAt}
        />

        <OtherRentalCompanies
          title={`Car Rental providers in ${props.locationName}`}
          baseUrl={props.url}
          locationName={props.locationName}
          locale={props.locale}
          agencyList={agencyList}
          currentAgency=""
        />

        <ClassMap
          title={`${carArray[0].car.carclass} Car Rental Directions from ${props.locationName}`}
          subtitle=""
          cardata={carArray}
          airportNameData={airportNameData}
          airportData={airport}
          locale={props.locale}
          locationName={props.locationName}
        />
        {(props.classList.filter((item: string) => item !== props.class)
          .length > 0 ||
          props.hasElectricCars) && (
            <AvailableClasses
              title={`Other Rental Car Classes available in ${props.locationName}`}
              url={props.url}
              locationName={props.locationName}
              classList={props.classList.filter(
                (item: string) => item !== props.class
              )}
              hasElectricCars={props.hasElectricCars}
            />
          )}
        {props.brandList.length > 0 && (
          <AvailableBrands
            locationName={props.locationName}
            brandList={props.brandList}
            url={props.url}
          />
        )}
        <ClassCarFeatures
          locationName={props.locationName}
          carclass={carClassName}
          featureData={featureData}
        />
        <CheapCarsList
          title={`Cheapest ${carArray[0].car.carclass} Car Rentals in ${props.locationName}`}
          subtitle={`Find the cheapest ${carArray[0].car.carclass} Car rentals in ${props.locationName}.`}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={props.updatedAt}
        />
        {nearestAirports.length > 1 && (
          <AirportsNearYou
            currentAirport={airport.kayakId}
            locationName={props.locationName}
            airports={nearestAirports}
            heading={`Nearby Airports you can rent ${carArray[0].car.carclass} Car from`}
            subheading={`If you don't like the ${carArray[0].car.carclass} cars in ${props.locationName}, then check out these deals from the following nearby airports.`}
          />
        )}
        <PopularCarList
          title={`Most Popular ${carArray[0].car.carclass} Car Rentals in ${props.locationName}`}
          subtitle={`Find the most popular ${carArray[0].car.carclass} Car rentals in ${props.locationName}.`}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={props.updatedAt}
        />
        <CustomFAQ faqData={faqData} />
        <Footer />
      </main>
    </>
  );
};

export default ClassPage;

export async function getStaticProps({
  params,
  locale,
}: {
  params: { airport: string; class: string; };
  locale: string;
}) {
  const ctid = params.airport.split("-").pop();
  const ctidNum = parseInt(ctid!);
  if (Number.isNaN(ctidNum) || ctid !== ctidNum.toString()) {
    return { notFound: true };
  }

  const airportCode = params.airport.split("-").reverse()[2].toUpperCase();
  if (!/^[A-Z]{3}$/.test(airportCode)) {
    return { notFound: true };
  }

  const carClass = params.class
    .split("/")
    .pop()
    ?.toUpperCase()
    .replace(/-/g, "_");

  const { carDB } = await connectToCarDataDB();

  const foundAirport = await carDB.collection("destinations").findOne({
    ctid: parseInt(ctid!),
    kayakId: airportCode,
  });
  if (!foundAirport) {
    return { notFound: true };
  }

  const countryCode = foundAirport.cc === "US" ? "USA" : foundAirport.cc;
  const locationName = await `${convertEntityKeyToName(
    foundAirport.entityKey
  )} [${foundAirport.id}], ${countryCode}`;

  const airportData = await carDB
    .collection("parseddatas")
    .aggregate([
      {
        $match: { ctid: ctid, currency: "USD" },
      },
      {
        $group: {
          _id: null,
          ctid: { $first: "$ctid" },
          currency: { $first: "$currency" },
          citylat: { $first: "$citylat" },
          citylon: { $first: "$citylon" },
          distanceUnits: { $first: "$distanceUnits" },
          lowestPrice: { $first: "$lowestPrice" },
          car_array: {
            $addToSet: {
              $setUnion: [
                "$car_data",
                "$cheapest_cars",
                "$most_popular_cars",
                "$recommended_cars",
              ],
            },
          },
          updatedAt: { $first: "$updatedAt" },
        },
      },
      {
        $unwind: {
          path: "$car_array",
          includeArrayIndex: "arrayIndex",
        },
      },
      {
        $project: {
          _id: 0,
          ctid: 1,
          currency: 1,
          citylat: 1,
          citylon: 1,
          distanceUnits: 1,
          lowestPrice: 1,
          car_array: {
            $filter: {
              input: "$car_array",
              as: "car",
              cond: { $eq: ["$$car.car.category", carClass] },
            },
          },
          updatedAt: 1,
        },
      },
      {
        $addFields: {
          car_array_size: { $size: "$car_array" },
        },
      },
      { $sort: { car_array_size: -1 } },
      {
        $limit: 1,
      },
    ])
    .toArray();

  if (airportData === null) {
    console.error(
      "Error Fetching Data: ",
      params.airport,
      "(Invalid CTID and/or Currency combination)"
    );
  } else {
    console.log(`Generating Page: ${params.airport}/class/${params.class}`);
  }

  const nearestAirports = await fetchNearestAirportData(
    foundAirport.lat,
    foundAirport.lng
  );

  if (!airportData[0]?.car_array?.length) {
    return { notFound: true };
  }

  const carArray: CarData[] = airportData[0].car_array.map((car: any) => ({
    ...car, shareURL: car.shareURL ? Buffer.from(car.shareURL, "utf8").toString("base64") : car.shareURL,
  }));

  const carClassName: string = carArray[0]?.car?.carclass;

  const agencyList: Agency[] = await generateInnerAgencyLinks(ctid!);
  const classList: string[] = await generateInnerClassLinks(ctid!);
  const brandList: string[] = await generateInnerBrandLinks(ctid!);

  const electricCarCount = await returnElectricCarCount([ctid!]);
  const hasElectricCars: boolean = electricCarCount?.[0]?.electricCarCount > 1;

  const airportNameData: AirportNameData = {
    id: foundAirport.apicode,
    airportName: foundAirport.airportname,
    cityName: foundAirport.cityname,
    cityOnly: foundAirport.cityonly,
    country: foundAirport.country,
    countryCode: foundAirport.cc,
    regionCode: foundAirport.rc,
    region: foundAirport.region,
    shortCode: foundAirport.searchFormPrimary || "",
    shortName: foundAirport.searchFormSecondary || "",
    secondaryName: foundAirport.secondary || "",
    shortDisplayName: foundAirport.shortdisplayname || "",
    longName: foundAirport.smartyDisplay || "",
  };

  return {
    props: {
      airportNameData: JSON.stringify(airportNameData),
      nearestAirports: JSON.stringify(nearestAirports),
      carArray: JSON.stringify(carArray),
      classCode: carClass,
      airport: JSON.stringify(foundAirport),
      locationName: locationName.replace(/([a-z])([A-Z])/g, "$1 $2") || null,
      agencyList: JSON.stringify(agencyList),
      classList: classList,
      updatedAt: airportData[0].updatedAt.toISOString(),
      url: params.airport,
      class: params.class,
      locale: locale,
      carClassName: carClassName,
      brandList: brandList || null,
      hasElectricCars: hasElectricCars || false,
      rating: {
        ratingValue: parseFloat((Math.random() * (9.8 - 7.2) + 7.2).toFixed(1)),
        ratingCount: Math.ceil(Math.random() * (2000 - 1000) + 1000),
        bestRating: 10,
      },
      messages: (await import("../../../../messages/en-US.json")).default,
    },
    // revalidate: 60 * 60 * 24, // 1 day
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
  // const params: {
  //   params: {
  //     airport: string;
  //     class: string;
  //   };
  // }[] = [];
  // const { db } = await connectToDatabase();
  // const { carDB } = await connectToCarDataDB();

  // const locationsDataMongoFull = await db
  //   // .collection("destinations")
  //   .collection("destinations")
  //   .find({
  //     kayakType: "ap",
  //     cc: "US",
  //     entityKey: { $exists: true, $ne: null },
  //     country: "United States",
  //   })
  //   // You may need to adjust this number based on your data
  //   .toArray();

  // const ctidArray = locationsDataMongoFull.map((item: any) =>
  //   item.ctid.toString()
  // );

  // const invalidClass = [
  //   "SPECIAL",
  //   "SPECIAL_SUV",
  //   "SPECIAL_WAGON",
  //   "SPECIAL_SEDAN",
  //   "SPECIAL_COUPE",
  //   "SPECIAL_VAN",
  // ];

  // const validCtidArr = await carDB
  //   .collection("parseddatas")
  //   .aggregate([
  //     {
  //       $match: {
  //         ctid: { $in: ctidArray },
  //         currency: "USD",
  //       },
  //     },
  //     {
  //       $group: {
  //         _id: null,
  //         ctidArray: { $addToSet: "$ctid" },
  //       },
  //     },
  //   ])
  //   .toArray();

  // const validCtid = validCtidArr[0].ctidArray;

  // const locationsDataMongo = locationsDataMongoFull.filter(
  //   (item: any) =>
  //     ctidArray.includes(item.ctid.toString()) &&
  //     validCtid.includes(item.ctid.toString())
  // );

  // const classList = await generateFullClassLinksFromArray(validCtid);

  // for (const location of locationsDataMongo) {
  //   const airportUrl = constructLink(location);
  //   const airportCtid = location.ctid.toString();

  //   const classCodes: {
  //     ctid: string;
  //     classNames: string[];
  //   } = classList.find(
  //     (carClass: { ctid: string; classNames: string[]; }) =>
  //       carClass.ctid === airportCtid
  //   )!;
  //   // This will never return undefined because we are only looping through locations that have valid ctids
  //   if (classCodes?.classNames === undefined) continue;
  //   const filteredClass = classCodes.classNames.filter(
  //     (className: string) => !invalidClass.includes(className)
  //   );
  //   for (const classCode of filteredClass) {
  //     params.push({
  //       params: {
  //         airport: airportUrl,
  //         class: classCode.toLowerCase().replace(/_/g, "-"),
  //       },
  //     });
  //   }
  // }
  // console.log("Class Pages to Generate: ", params.length);

  // return {
  //   paths: params,
  //   fallback: false,
  // };
  // // FIXME: SET FALLBACK BACK TO FALSE WHEN THE BELOW ISSUE IS FIXED
  // // FIXME: Whoever is working on this after me, please find a way to ensure that the results the query gives are the same as the results the query returns in getStaticProps.
}
